import React, {
  ChangeEvent,
  CSSProperties,
  ReactElement,
  useEffect,
  useState,
} from 'react'
import { getCurrencySymbol, roundDecimalValue } from '../helpers/currencyHelper'
import { onlyString } from '../helpers/dataTransformer'
import { CurrencyCode } from '../types/API'
import FieldStyles from '../components/FieldStyles'

type CurrencyInput = string | string[]

export type CurrencyFieldProps = {
  name?: string
  placeholder?: string
  isError?: boolean
  currency: CurrencyCode
  label?: string
  onChange?: (value: string) => void
  initValue?: CurrencyInput
  isSorting?: boolean
  isMulti?: boolean
  subtitle?: string | null
  className?: string
  style?: CSSProperties
}

const changeToString = (input: CurrencyInput | number): string => {
  // just in case we get sent a number
  if (typeof input === 'number') return input.toString()
  if (typeof input === 'string') return input
  return input.join(',')
}
const cleanInput = (
  input: CurrencyInput,
  isMulti: boolean = false,
): CurrencyInput => {
  let cleanRegEx = /[^0-9.]/g
  if (isMulti) cleanRegEx = /[^0-9,.]/g
  const allowedCharacters = changeToString(input).replace(cleanRegEx, '')
  if (allowedCharacters.split(',').length > 1) {
    return allowedCharacters.split(',')
  }
  return allowedCharacters
}
export const formatInput = (
  input: CurrencyInput,
  isSorting?: boolean,
): CurrencyInput => {
  if (input === undefined || input === '') return input
  if (typeof input === 'string') return roundDecimalValue(input)
  const inputArray = input
    .filter((value) => !Number.isNaN(parseFloat(value)))
    .map((value: string) => roundDecimalValue(value))
  if (isSorting) inputArray.sort((a, b) => Number(a) - Number(b))
  return inputArray
}

function CurrencyField(props: CurrencyFieldProps): ReactElement {
  const {
    name,
    placeholder,
    isError,
    currency,
    label,
    onChange,
    initValue,
    isSorting,
    isMulti,
    subtitle,
    className,
    style,
  } = props

  const [inputValue, setInputValue] = useState<CurrencyInput>('')

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const value = cleanInput(event.target.value, isMulti)
    setInputValue(value)
    if (onChange) onChange(typeof value === 'string' ? value : value.join(','))
  }

  useEffect(() => {
    if (initValue) setInputValue(cleanInput(initValue, isMulti))
  }, [])

  const renderSubtitle = subtitle
    || (isMulti
      ? 'Add the values you wish to display, separated by commas, e.g. 10,20,30,40 etc.'
      : null)

  return (
    <FieldStyles label={label} subtitle={renderSubtitle}>
      <div className="widget-wrapper">
        <div className="widget">{getCurrencySymbol(currency)}</div>
        <input
          style={style}
          id={label ? onlyString(label) : undefined}
          type="text"
          name={name}
          placeholder={placeholder}
          className={`${className} styled-input with-widget ${isError ? 'error' : ''
          }`}
          onChange={handleChange}
          value={inputValue}
          onBlur={(): void => setInputValue(formatInput(inputValue, isSorting))}
        />
      </div>
    </FieldStyles>
  )
}

export default CurrencyField
